import { useState } from 'react';
import { motion } from 'framer-motion';
import { Send, Loader2, Upload, X, Heart, Clock, Award, FileCheck } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { Checkbox } from "@/components/ui/checkbox";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Volunteers() {
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    postcode: '',
    experience: '',
    social_media: '',
    description: '',
    availability: '',
    has_certificates: false,
    certificate_types: '',
    preferred_role: '',
    additional_info: '',
    agrees_to_terms: false,
  });
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value,
    });
  };

  const handleFileChange = (e) => {
    const newFiles = Array.from(e.target.files);
    setFiles([...files, ...newFiles]);
  };

  const removeFile = (index) => {
    setFiles(files.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.agrees_to_terms) {
      toast.error('You must agree to the terms and conditions');
      return;
    }
    
    setLoading(true);
    
    try {
      const submitData = new FormData();
      submitData.append('application_type', 'volunteers');
      
      Object.keys(formData).forEach(key => {
        submitData.append(key, formData[key]);
      });
      
      files.forEach(file => {
        submitData.append('files', file);
      });
      
      await axios.post(`${API}/applications`, submitData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success('Application submitted successfully! We\'ll be in touch.');
      
      setFormData({
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        address: '',
        city: '',
        state: '',
        postcode: '',
        experience: '',
        social_media: '',
        description: '',
        availability: '',
        has_certificates: false,
        certificate_types: '',
        preferred_role: '',
        additional_info: '',
        agrees_to_terms: false,
      });
      setFiles([]);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-ff-blue pt-20" data-testid="volunteers-page">
      <section className="py-16 md:py-24">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-12"
          >
            <span className="font-mono text-sm text-ff-brown uppercase tracking-widest block mb-4">
              Applications
            </span>
            <h1 className="font-display text-4xl sm:text-5xl text-ff-brown mb-4">
              <span className="text-ff-brown">Volunteers</span>
            </h1>
            <p className="text-ff-brown/70 max-w-2xl mx-auto">
              Join our incredible volunteer team and be part of the magic that makes 
              Frequency Forest possible.
            </p>
          </motion.div>

          {/* Volunteer Guide Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="mb-12"
          >
            <div className="glass-card p-8 mb-6">
              <div className="flex items-center gap-4 mb-6">
                <Heart className="w-8 h-8 text-ff-brown" />
                <h2 className="font-display text-2xl text-ff-brown">Volunteering Application Guide</h2>
              </div>
              <p className="text-ff-brown/70 mb-4">
                Frequency Forest takes place over the Easter Long Weekend, 2nd to 6th April 2026, 
                set in the stunning NSW bushland.
              </p>
              <p className="text-ff-brown/70">
                Our mission is to create a fun, safe, and memorable experience while supporting 
                the Australian music and arts community. It takes an extraordinary number of 
                dedicated people to bring this festival to life, and we truly appreciate you 
                wanting to be one of those people.
              </p>
            </div>

            <div className="glass-card p-8 mb-6 border-l-4 border-yellow-500">
              <h3 className="font-display text-xl text-ff-brown mb-4">IMPORTANT</h3>
              <p className="text-ff-brown/70 mb-4">
                We may require volunteers to purchase a deposit ticket up front, which will be 
                refunded after completing your shifts at the festival.
              </p>
              <p className="text-ff-brown/70">
                Information on how to purchase a volunteer ticket will be sent with your 
                confirmation. Take your time to fill in the form completely – 
                <strong className="text-ff-brown"> incomplete applications will not be accepted</strong>.
              </p>
            </div>

            <div className="glass-card p-8 mb-6 border-l-4 border-red-500">
              <h3 className="font-display text-xl text-ff-brown mb-4">
                Your application will not be accepted without:
              </h3>
              <ul className="text-ff-brown/70 space-y-2">
                <li className="flex items-center gap-3">
                  <div className="w-2 h-2 bg-ff-secondary rounded-full" />
                  All current contact info (address, phone number, email)
                </li>
                <li className="flex items-center gap-3">
                  <div className="w-2 h-2 bg-ff-secondary rounded-full" />
                  Digital copy or clear photo of photo ID (Passport or Australian License)
                </li>
                <li className="flex items-center gap-3">
                  <div className="w-2 h-2 bg-ff-secondary rounded-full" />
                  Uploads of any relevant certificates
                </li>
              </ul>
            </div>

            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <div className="glass-card p-6">
                <Clock className="w-6 h-6 text-ff-brown mb-4" />
                <h3 className="font-display text-lg text-ff-brown mb-3">What We Expect</h3>
                <ul className="text-ff-brown/70 text-sm space-y-2">
                  <li>• Show up on time for allocated shifts</li>
                  <li>• Contact team leader before the event if there are issues</li>
                  <li>• Usually 12 hours of work during the event</li>
                  <li>• Or 2-3 days for setup/packdown (meals included)</li>
                  <li>• Bring your own sleeping gear, tent, and closed shoes</li>
                </ul>
              </div>
              
              <div className="glass-card p-6">
                <Award className="w-6 h-6 text-ff-brown mb-4" />
                <h3 className="font-display text-lg text-ff-brown mb-3">Selection Process</h3>
                <ul className="text-ff-brown/70 text-sm space-y-2">
                  <li>• We prefer applicants with experience</li>
                  <li>• Mention all previous experience</li>
                  <li>• Attach certificates (RSA, White Card, First Aid)</li>
                  <li>• No experience? Everyone starts somewhere!</li>
                  <li>• You may not get your desired role without certs</li>
                </ul>
              </div>
            </div>

            <div className="glass-card p-8">
              <div className="flex items-center gap-4 mb-4">
                <FileCheck className="w-6 h-6 text-ff-brown" />
                <h3 className="font-display text-lg text-ff-brown">Contracts & Conditions</h3>
              </div>
              <p className="text-ff-brown/70 text-sm">
                Successful applicants will be sent a contract to complete and return for shift 
                allocation. All workers and volunteers must read and follow all general rules 
                and terms and conditions of entry set by Frequency Forest. An induction must 
                be completed before arriving at the site.
              </p>
            </div>
          </motion.div>

          {/* Application Form */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="glass-card p-8"
          >
            <h2 className="font-display text-2xl text-ff-brown mb-6">Volunteer Application Form</h2>
            
            <form onSubmit={handleSubmit} className="space-y-6" data-testid="volunteer-form">
              {/* Personal Information */}
              <div>
                <h3 className="font-display text-xl text-ff-brown mb-4">Contact Information</h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="form-label">First Name *</label>
                    <input
                      type="text"
                      name="first_name"
                      value={formData.first_name}
                      onChange={handleChange}
                      className="form-input"
                      required
                    />
                  </div>
                  <div>
                    <label className="form-label">Last Name *</label>
                    <input
                      type="text"
                      name="last_name"
                      value={formData.last_name}
                      onChange={handleChange}
                      className="form-input"
                      required
                    />
                  </div>
                  <div>
                    <label className="form-label">Email *</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      className="form-input"
                      required
                    />
                  </div>
                  <div>
                    <label className="form-label">Phone *</label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className="form-input"
                      required
                    />
                  </div>
                </div>
                
                <div className="mt-4">
                  <label className="form-label">Address *</label>
                  <input
                    type="text"
                    name="address"
                    value={formData.address}
                    onChange={handleChange}
                    className="form-input"
                    required
                  />
                </div>
                
                <div className="grid md:grid-cols-3 gap-4 mt-4">
                  <div>
                    <label className="form-label">City *</label>
                    <input
                      type="text"
                      name="city"
                      value={formData.city}
                      onChange={handleChange}
                      className="form-input"
                      required
                    />
                  </div>
                  <div>
                    <label className="form-label">State *</label>
                    <input
                      type="text"
                      name="state"
                      value={formData.state}
                      onChange={handleChange}
                      className="form-input"
                      placeholder="NSW"
                      required
                    />
                  </div>
                  <div>
                    <label className="form-label">Postcode *</label>
                    <input
                      type="text"
                      name="postcode"
                      value={formData.postcode}
                      onChange={handleChange}
                      className="form-input"
                      required
                    />
                  </div>
                </div>
              </div>

              {/* Social Media */}
              <div>
                <label className="form-label">Facebook Profile (optional)</label>
                <input
                  type="text"
                  name="social_media"
                  value={formData.social_media}
                  onChange={handleChange}
                  className="form-input"
                  placeholder="Facebook profile URL"
                />
              </div>

              {/* Experience */}
              <div>
                <label className="form-label">Previous Volunteering Experience *</label>
                <textarea
                  name="experience"
                  value={formData.experience}
                  onChange={handleChange}
                  className="form-input min-h-[120px] resize-none"
                  placeholder="Tell us about any previous festival/event volunteer experience..."
                  required
                />
              </div>

              {/* Preferred Role */}
              <div>
                <label className="form-label">Preferred Role/Area *</label>
                <select
                  name="preferred_role"
                  value={formData.preferred_role}
                  onChange={handleChange}
                  className="form-input"
                  required
                >
                  <option value="">Select a role...</option>
                  <option value="gate">Gate & Entry</option>
                  <option value="bar">Bar (RSA required)</option>
                  <option value="setup">Setup/Packdown</option>
                  <option value="sanctuary">Sanctuary/Welfare</option>
                  <option value="cleaning">Site Cleaning</option>
                  <option value="parking">Parking & Traffic</option>
                  <option value="info">Info Tent</option>
                  <option value="roving">Roving Assistant</option>
                  <option value="other">Other (specify below)</option>
                </select>
              </div>

              {/* Availability */}
              <div>
                <label className="form-label">Availability *</label>
                <textarea
                  name="availability"
                  value={formData.availability}
                  onChange={handleChange}
                  className="form-input min-h-[80px] resize-none"
                  placeholder="When are you available? Can you do setup/packdown? Any dates you can't do?"
                  required
                />
              </div>

              {/* Why You Want to Volunteer */}
              <div>
                <label className="form-label">Why do you want to volunteer? *</label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  className="form-input min-h-[100px] resize-none"
                  placeholder="Tell us why you want to be part of the Frequency Forest team..."
                  required
                />
              </div>

              {/* Certificates */}
              <div>
                <div className="flex items-center space-x-3 mb-4">
                  <Checkbox
                    id="has_certificates"
                    checked={formData.has_certificates}
                    onCheckedChange={(checked) => 
                      setFormData({ ...formData, has_certificates: checked })
                    }
                  />
                  <label htmlFor="has_certificates" className="text-ff-brown/70 text-sm">
                    I have relevant certificates (RSA, First Aid, White Card, etc.)
                  </label>
                </div>
                {formData.has_certificates && (
                  <div>
                    <label className="form-label">List your certificates</label>
                    <input
                      type="text"
                      name="certificate_types"
                      value={formData.certificate_types}
                      onChange={handleChange}
                      className="form-input"
                      placeholder="RSA, First Aid Level 2, White Card, etc."
                    />
                  </div>
                )}
              </div>

              {/* Additional Info */}
              <div>
                <label className="form-label">Additional Information</label>
                <textarea
                  name="additional_info"
                  value={formData.additional_info}
                  onChange={handleChange}
                  className="form-input min-h-[80px] resize-none"
                  placeholder="Anything else you'd like us to know? Special skills, dietary requirements, etc."
                />
              </div>

              {/* File Upload */}
              <div>
                <label className="form-label">Upload Files (Photo ID & Certificates) *</label>
                <div className="border-2 border-dashed border-white/10 rounded-sm p-6 text-center hover:border-ff-secondary/50 transition-colors">
                  <input
                    type="file"
                    multiple
                    onChange={handleFileChange}
                    className="hidden"
                    id="file-upload"
                    accept=".pdf,.jpg,.jpeg,.png"
                  />
                  <label htmlFor="file-upload" className="cursor-pointer">
                    <Upload className="w-8 h-8 text-ff-text-muted mx-auto mb-2" />
                    <p className="text-ff-brown/70 text-sm">
                      Click to upload Photo ID and any certificates
                    </p>
                    <p className="text-ff-text-muted text-xs mt-1">
                      PDF, JPG, PNG (Max 10MB each)
                    </p>
                  </label>
                </div>
                
                {files.length > 0 && (
                  <div className="mt-4 space-y-2">
                    {files.map((file, index) => (
                      <div key={index} className="flex items-center justify-between bg-white/5 p-3 rounded">
                        <span className="text-ff-brown/70 text-sm truncate">{file.name}</span>
                        <button
                          type="button"
                          onClick={() => removeFile(index)}
                          className="text-red-500 hover:text-red-400"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Terms Agreement */}
              <div className="border-t border-white/10 pt-6">
                <div className="flex items-start space-x-3">
                  <Checkbox
                    id="agrees_to_terms"
                    checked={formData.agrees_to_terms}
                    onCheckedChange={(checked) => 
                      setFormData({ ...formData, agrees_to_terms: checked })
                    }
                    required
                  />
                  <label htmlFor="agrees_to_terms" className="text-ff-brown/70 text-sm">
                    I understand that submitting this application does not guarantee acceptance. 
                    I agree to follow all Frequency Forest rules, terms, and conditions if accepted. 
                    I confirm all information provided is accurate and I have uploaded my photo ID. *
                  </label>
                </div>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={loading}
                className="btn-primary w-full flex items-center justify-center gap-2"
                data-testid="volunteer-submit-btn"
              >
                {loading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <Send className="w-5 h-5" />
                )}
                Submit Application
              </button>
            </form>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
